import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  hidePostalCode: true,
};

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Extract job + user details
  const amount = location.state?.amount || 50;
  const customerId =
    location.state?.customerId || localStorage.getItem("customerId");
  const serviceType = location.state?.serviceType || "Service";
  const jobTitle = serviceType;
  const description = location.state?.description || "No description provided";
  const scheduledAt = location.state?.scheduledAt || new Date();
  const locationString = location.state?.location || "Not Provided";
  const email =
    location.state?.email || localStorage.getItem("userEmail") || "";

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // ✅ Extract city, province, postalCode from address
  const extractAddressParts = (address) => {
    let city = "",
      province = "",
      postalCode = "";
    try {
      const parts = address.split(",");
      if (parts.length >= 3) {
        city = parts[1]?.trim() || "";
        province = parts[2]?.trim() || "";
        const postalCodeMatch = address.match(/[A-Z]\d[A-Z][ -]?\d[A-Z]\d/i);
        postalCode = postalCodeMatch?.[0]?.toUpperCase() || "";
      }
    } catch (err) {
      console.warn("⚠️ Address parsing failed:", err);
    }
    return { city, province, postalCode };
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setProcessing(true);
  setError("");

  if (!stripe || !elements) {
    setError("Stripe not initialized. Please wait a moment and retry.");
    setProcessing(false);
    return;
  }

  if (!customerId) {
    setError("Session expired. Please log in again.");
    setProcessing(false);
    return;
  }

  try {
    // ✅ 1. Create PaymentIntent
    const intentRes = await axios.post(`${API_BASE}/api/payment/create-intent`, {
      amount,
      customerId,
      currency: "cad",
    });

    const clientSecret = intentRes.data.clientSecret || intentRes.data.ClientSecret;
    if (!clientSecret) throw new Error("Missing client secret from backend.");

    // ✅ 2. Confirm Card Payment
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: { name: "Customer", email },
      },
    });

    if (result.error) {
      setError(result.error.message);
      setProcessing(false);
      return;
    }

    // ✅ 3. Handle success (both succeeded or requires_capture)
    if (["succeeded", "requires_capture"].includes(result.paymentIntent.status)) {
      console.log("✅ Payment successful, creating job...");
      setSuccess(true);
      setProcessing(false);

      // Extract address parts
      const { city, province, postalCode } = extractAddressParts(locationString);
      const lat = parseFloat(localStorage.getItem("userLat")) || null;
      const lng = parseFloat(localStorage.getItem("userLng")) || null;

      // ✅ 4. Create job payload
      const jobPayload = {
        customerId,
        serviceType,
        jobTitle,
        description,
        budget: amount,
        location: locationString,
        scheduledAt,
        email,
        city,
        province,
        street: "",
        postalCode,
        latitude: lat,
        longitude: lng,
        stripePaymentIntentId: result.paymentIntent.id,
        paymentStatus: "holding",
      };

      console.log("📦 Sending Job Payload:", jobPayload);

      // ✅ 5. Post job
      const jobRes = await axios.post(`${API_BASE}/api/customers/jobs/post-job`, jobPayload);
      console.log("📩 Job creation response:", jobRes.data);

      const createdJobId = jobRes.data?.jobId || jobRes.data?._id;

      if (!createdJobId) {
        console.warn("⚠️ Job ID missing in response. Skipping redirect.");
        setError("Job created but missing ID. Please check Your Jobs page.");
        return;
      }

      // ✅ 6. Redirect after short delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      console.log("➡️ Redirecting to success page with jobId:", createdJobId);

      navigate("/job-posted-success", {
        state: { jobId: createdJobId, scheduledAt, location: locationString },
      });
    }
  } catch (err) {
    console.error("❌ Payment or job creation failed:", err.response?.data || err.message);
    setError("Something went wrong. Please try again.");
    setProcessing(false);
  }
};

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-200"
      >
        <h2 className="text-2xl font-bold text-center mb-3 text-gray-800">
          🔐 Secure Payment
        </h2>

        <p className="text-center text-sm text-gray-600 mb-1">
          You're paying{" "}
          <span className="font-semibold text-green-600">${amount} CAD</span>
        </p>

        <div className="text-xs text-gray-500 text-center mb-4 mt-1 leading-snug bg-blue-50 p-3 rounded-md border border-blue-200">
          Your payment is securely held and{" "}
          <span className="font-semibold text-blue-700">
            only released to the worker
          </span>{" "}
          once the job is completed and confirmed.
        </div>

        <div className="flex justify-center items-center gap-4 mb-3">
          <img
            src="https://img.icons8.com/color/48/000000/visa.png"
            alt="Visa"
            className="h-6"
          />
          <img
            src="https://img.icons8.com/color/48/000000/mastercard-logo.png"
            alt="Mastercard"
            className="h-6"
          />
          <img
            src="https://img.icons8.com/color/48/000000/amex.png"
            alt="Amex"
            className="h-6"
          />
          <img
            src="https://img.icons8.com/color/48/000000/apple-pay.png"
            alt="Apple Pay"
            className="h-6"
          />
        </div>

        <div className="mb-4 border rounded p-3 shadow-sm">
          <CardElement options={CARD_OPTIONS} />
        </div>

        <button
          type="submit"
          disabled={!stripe || processing}
          className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition duration-300"
        >
          {processing ? "Processing..." : `Pay $${amount} CAD`}
        </button>

        {error && (
          <div className="text-red-500 text-sm mt-3 text-center">{error}</div>
        )}
        {success && (
          <div className="text-green-600 text-center mt-4 font-medium">
            ✅ Payment Successful!
          </div>
        )}
      </form>
    </div>
  );
};

const Payment = () => (
  <Elements stripe={stripePromise}>
    <CheckoutForm />
  </Elements>
);

export default Payment;
